/**
 * <km-strip-reset> — Sends every sideways image strip back to its first photo
 * each time a page is shown, including when a visitor returns with the back
 * button (the browser would otherwise restore where they left off).
 *
 * Browsers can restore a strip's position late (after its lazy photos load, or
 * when it first scrolls into view), so each strip is reset again at those
 * moments until the visitor touches it themselves.
 */
const STRIP_SELECTOR = '[class*="overflow-x-auto"], [class*="snap-x"]';
const USER_EVENTS = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

class KmStripReset extends HTMLElement {
  #ac;
  #observer;
  #touched = new WeakSet();
  #seen = new WeakSet();
  #shownAt = performance.now();

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // Only the first time a strip comes into view after the page is shown.
        if (!entry.isIntersecting || this.#seen.has(entry.target)) continue;
        this.#seen.add(entry.target);
        this.#resetStrip(entry.target);
      }
    });

    for (const strip of this.#strips()) {
      this.#observer.observe(strip);
      for (const type of USER_EVENTS) {
        strip.addEventListener(type, () => this.#touched.add(strip), { passive: true, signal });
      }
      // Late-loading photos change the strip's width, which is when browsers re-apply a saved position.
      strip.addEventListener('load', () => {
        if (performance.now() - this.#shownAt < 2500) this.#resetStrip(strip);
      }, { capture: true, signal });
    }

    window.addEventListener('pageshow', () => {
      this.#touched = new WeakSet();
      this.#seen = new WeakSet();
      this.#shownAt = performance.now();
      for (const strip of this.#strips()) {
        this.#observer.unobserve(strip);
        this.#observer.observe(strip);
      }
      this.#resetAll();
      requestAnimationFrame(() => this.#resetAll());
    }, { signal });
    window.addEventListener('load', () => this.#resetAll(), { signal });
    // Pressing a strip's Scroll button counts as the visitor moving it themselves.
    document.addEventListener('click', (event) => {
      const cue = event.target instanceof Element ? event.target.closest('km-scroll-cue') : null;
      if (!cue) return;
      for (const strip of cue.querySelectorAll(STRIP_SELECTOR)) this.#touched.add(strip);
    }, { capture: true, signal });
    this.#resetAll();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #strips() {
    return document.querySelectorAll(STRIP_SELECTOR);
  }

  #resetAll() {
    for (const strip of this.#strips()) this.#resetStrip(strip);
  }

  #resetStrip(strip) {
    if (this.#touched.has(strip) || strip.scrollLeft === 0) return;
    // Snap points can pull a strip back to the saved photo, so lift snapping for the jump.
    const snap = strip.style.scrollSnapType;
    strip.style.scrollSnapType = 'none';
    strip.scrollLeft = 0;
    requestAnimationFrame(() => {
      strip.style.scrollSnapType = snap;
    });
  }
}

if (!customElements.get('km-strip-reset')) customElements.define('km-strip-reset', KmStripReset);
